<template>
  <!--
    Кнопка добавления колонки комментария. Лежит отдельным слоем поверх таблицы, а не
    внутри строки: у ячеек стоит overflow hidden, поэтому прижать кнопку к правому краю
    видимой области изнутри строки не получится. Слой не прокручивается вбок, значит
    right это и есть правый край видимой части таблицы.
  -->
  <div ref="rootEl" class="qtl-add-comment-layer">
    <button
      v-if="shown"
      type="button"
      class="qtl-add-comment"
      :style="{ top: btnTop + 'px', right: btnRight + 'px' }"
      @mousedown.stop.prevent
      @click.stop="onClick"
    >
      <svg class="qtl-add-comment__pencil" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15.916 4.96744C16.1579 4.72534 16.1062 4.28108 15.8003 3.97521L14.0227 2.19756C13.7167 1.89167 13.2724 1.83978 13.0303 2.0819L11.5684 3.54388L14.4539 6.42944L15.916 4.96744Z" fill="currentColor"/>
        <path d="M1.82721 16.3264C2.45354 16.1893 4.35117 15.6936 5.01569 15.5191C5.16843 15.4791 5.30784 15.399 5.41938 15.2871L13.3689 7.33816L10.6601 4.62952L2.71052 12.5784C2.59861 12.69 2.51861 12.8294 2.47849 12.9822C2.30396 13.6468 1.80836 15.5448 1.67204 16.1717C1.6624 16.215 1.67561 16.2602 1.70704 16.2915C1.73847 16.323 1.78387 16.3361 1.82721 16.3264Z" fill="currentColor"/>
      </svg>
      <svg class="qtl-add-comment__plus" width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0.875 4.375L7.875 4.375" stroke="currentColor" stroke-width="1.6875" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M4.375 0.875L4.375 7.875" stroke="currentColor" stroke-width="1.6875" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <q-tooltip :delay="300" anchor="top middle" self="center middle" class="qtl-add-comment-tooltip">
        {{ $t('add_comment') }}
      </q-tooltip>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

const props = defineProps<{
  /** можно ли вообще показывать кнопку (права, колонка ещё не включена и т.п.) */
  enabled: boolean;
  /** сама таблица, по её строкам ищем строку под курсором */
  tableEl?: HTMLTableElement | null;
  /** место, где крутится таблица */
  scrollEl?: HTMLElement | null;
  /** отступ сверху до верхней границы таблицы (под формулами) */
  topOffset?: number;
  /** ниже какой координаты внутри слоя кнопка может находиться (низ закреплённой шапки) */
  minTop?: number;
}>();

const emit = defineEmits<{ (e: "add", entityId: number): void }>();

// 4 + 18 + 4 по макету, высота нужна до отрисовки, чтобы кнопка сразу встала на место
const BTN_HEIGHT = 26;
const GAP_BOTTOM = 1;
const GAP_RIGHT = 2;

const rootEl = ref<HTMLElement | null>(null);
const btnTop = ref(0);
const btnRight = ref(GAP_RIGHT);
const hoveredEntityId = ref(0);

const shown = computed(() => props.enabled && hoveredEntityId.value > 0);

// строка, которой мы держим подсветку: пока курсор на кнопке, штатный :hover у строки пропадает
let hoveredRow: HTMLElement | null = null;

function setHoveredRow(tr: HTMLElement | null) {
  if (hoveredRow === tr) return;
  if (hoveredRow) hoveredRow.classList.remove("qtl-row--hover");
  hoveredRow = tr;
  if (hoveredRow) hoveredRow.classList.add("qtl-row--hover");
}

function rowUnderPoint(y: number): HTMLElement | null {
  const table = props.tableEl;
  if (!table) return null;
  const trs = table.querySelectorAll<HTMLElement>("tbody tr.qtl-row");
  for (const tr of trs) {
    const r = tr.getBoundingClientRect();
    if (y >= r.top && y < r.bottom) return tr;
  }
  return null;
}

function clear() {
  setHoveredRow(null);
  hoveredEntityId.value = 0;
}

function place(tr: HTMLElement | null) {
  const root = rootEl.value;
  if (!tr || !root) {
    clear();
    return;
  }

  const entityId = Number(tr.dataset.entityId || 0);
  if (!entityId) {
    clear();
    return;
  }

  const rootRect = root.getBoundingClientRect();
  const rowRect = tr.getBoundingClientRect();
  const scroll = props.scrollEl;
  const limit = scroll ? scroll.getBoundingClientRect() : rootRect;

  // Границы считаем по содержимому скролла (clientWidth/clientHeight), а не по его
  // прямоугольнику: иначе кнопка ложится на полосы прокрутки и перекрывает их.
  const visibleRight = scroll ? limit.left + scroll.clientWidth : rootRect.right;
  const visibleBottom = scroll ? limit.top + scroll.clientHeight : rootRect.bottom;

  // строка ушла за пределы видимой части - кнопку не показываем
  if (rowRect.bottom <= limit.top || rowRect.top >= visibleBottom) {
    clear();
    return;
  }

  const raw = rowRect.bottom - rootRect.top - BTN_HEIGHT - GAP_BOTTOM;
  const min = (props.topOffset ?? 0) + (props.minTop ?? 0);
  const max = visibleBottom - rootRect.top - BTN_HEIGHT - GAP_BOTTOM;
  btnTop.value = Math.max(min, Math.min(raw, max));
  btnRight.value = Math.max(GAP_RIGHT, Math.round(rootRect.right - visibleRight) + GAP_RIGHT);
  hoveredEntityId.value = entityId;
  setHoveredRow(tr);
}

function onHostMouseMove(ev: MouseEvent) {
  if (!props.enabled) {
    clear();
    return;
  }
  place(rowUnderPoint(ev.clientY));
}

function onHostMouseLeave(ev: MouseEvent) {
  // уход курсора на саму кнопку не считаем уходом из таблицы
  const to = ev.relatedTarget as HTMLElement | null;
  if (to && rootEl.value && rootEl.value.contains(to)) return;
  clear();
}

function onScroll() {
  if (!hoveredRow) return;
  place(hoveredRow);
}

function onClick() {
  const id = hoveredEntityId.value;
  if (!id) return;
  clear();
  emit("add", id);
}

let host: HTMLElement | null = null;
let attachedScroll: HTMLElement | null = null;

function attachHost() {
  detachHost();
  host = rootEl.value?.parentElement || null;
  if (!host) return;
  host.addEventListener("mousemove", onHostMouseMove);
  host.addEventListener("mouseleave", onHostMouseLeave);
}

function detachHost() {
  if (!host) return;
  host.removeEventListener("mousemove", onHostMouseMove);
  host.removeEventListener("mouseleave", onHostMouseLeave);
  host = null;
}

function attachScroll(el: HTMLElement | null | undefined) {
  if (attachedScroll === el) return;
  if (attachedScroll) attachedScroll.removeEventListener("scroll", onScroll);
  attachedScroll = el || null;
  attachedScroll?.addEventListener("scroll", onScroll, { passive: true });
}

onMounted(() => {
  attachHost();
  attachScroll(props.scrollEl);
});

watch(() => props.scrollEl, (el) => attachScroll(el));
watch(() => props.enabled, (on) => { if (!on) clear(); });

onBeforeUnmount(() => {
  clear();
  detachHost();
  if (attachedScroll) attachedScroll.removeEventListener("scroll", onScroll);
  attachedScroll = null;
});
</script>

<style lang="scss" scoped>
.qtl-add-comment-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 6;
}

.qtl-add-comment {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 8px 0 0 8px;
  background: #FFF;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.20);
  color: #979797;
  cursor: pointer;
  pointer-events: auto;
}

.qtl-add-comment:hover {
  color: #3F884F;
}

.qtl-add-comment__pencil,
.qtl-add-comment__plus {
  display: block;
  flex-shrink: 0;
}

.qtl-add-comment__pencil {
  position: relative;
  left: 0;
}

.qtl-add-comment__plus {
  position: absolute;
  right: 2px;
  bottom: 3px;
}
</style>

<style lang="scss">
.q-tooltip.qtl-add-comment-tooltip {
  border-radius: 8px;
  background: var(--color-tooltip, rgba(92, 92, 92, 0.90));
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.20);
  padding: 2px 8px;
  color: #FFF;
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
  max-width: none;
}
</style>
